import React from 'react';

export type NvEmojiProps = {
  icon: string, // Emoji name or unicode
  size?: string, // Sizing similar to NvIcon
  label?: string, // Accessible label
  className?: string,
  style?: React.CSSProperties,
  onClick?: React.MouseEventHandler<HTMLSpanElement>,
} & React.ComponentProps<'span'>;

// Simple emoji map (expand as needed)
const emojiMap: Record<string, string> = {
  light: '💡',
  wave: '👋',
  smile: '😊',
  thumbs_up: '👍',
  heart: '❤️',
  fire: '🔥',
  star: '⭐',
  check: '✅',
  warning: '⚠️',
  info: 'ℹ️',
  question: '❓',
};

// Size map (px values similar to NvIcon)
const sizeMap: Record<string, string> = {
  'xss-smallest': '12px',
  'xs-smallest': '14px',
  smallest: '16px',
  small: '20px',
  medium: '24px',
  'medium-large': '28px',
  'medium-larger': '32px',
  large: '36px',
  larger: '48px',
  largest: '60px',
  'super-large': '80px',
  'ultra-large': '100px',
};

const NvEmoji = React.forwardRef<HTMLSpanElement, NvEmojiProps>((props, ref) => {
  const {
    icon,
    size = 'medium', // Default size
    label,
    className,
    style,
    onClick,
    ...restProps
  } = props;

  // Use unicode if not found in map
  const emoji = emojiMap[icon] || icon;
  const fontSize = sizeMap[size] || size || '24px';

  return (
    <span
      ref={ref}
      role={onClick ? 'button' : 'img'}
      aria-label={label}
      aria-hidden={label ? 'false' : 'true'}
      className={`nv-emoji nv-emoji-${size}${className ? ` ${className}` : ''}`}
      style={{ fontSize, ...style }}
      onClick={onClick}
      {...(onClick ? { tabIndex: 0 } : {})}
      onKeyPress={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e as any);
        }
      } : undefined}
      {...restProps}
    >
      {emoji}
    </span>
  );
});

export default NvEmoji;
